import { useMemo, useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { customiseDraft, optionPrice } from '@/lib/costingSheetState'
import { multiplierValid, variantCost, type RecipeLineDraft, type StockItemRef, type VariantDraft } from '@/lib/recipeEditor'
import { cn } from '@/lib/utils'
import type { RecipeVariant } from '@/types'
import { RecipeEditor } from './RecipeEditor'

/**
 * Variants (F-MOD-N11): one card per modifier group attached to the product,
 * one row per option. "Same as base" means the option changes nothing about
 * what is deducted; "Customise" reveals a multiplier ("Whole Cake makes 8×
 * the base") and ingredient rows that ADD to or REPLACE the base recipe.
 *
 * An option's recipe is shared by every product carrying its group — that is
 * the point (Iced on all seven lattes at once) — so turning Customise ON
 * starts from the saved recipe, and turning it OFF on an option that has one
 * asks first and names how many items it affects (audit U-1).
 */
export function VariantsSection({
  variants,
  drafts,
  onChange,
  baseLines,
  yieldN,
  price,
  items,
  itemList,
  formatMoney,
  onCreateIngredient,
  onSaveCost,
}: {
  variants: RecipeVariant[]
  /** Option id → draft; absent means "same as base". */
  drafts: Record<string, VariantDraft>
  onChange: (optionId: string, draft: VariantDraft | null) => void
  baseLines: RecipeLineDraft[]
  yieldN: number
  price: number
  items: Record<string, StockItemRef>
  itemList: StockItemRef[]
  formatMoney: (n: number) => string
  onCreateIngredient?: (optionId: string, query: string, rowKey: string | null) => void
  onSaveCost?: (item: StockItemRef, costPerStockUnit: number) => Promise<boolean>
}) {
  const [confirmOff, setConfirmOff] = useState<RecipeVariant | null>(null)
  const groups = useMemo(() => {
    const m = new Map<string, { name: string; priceMode: 'add' | 'replace'; count: number; options: RecipeVariant[] }>()
    for (const v of variants) {
      if (!m.has(v.group_id)) m.set(v.group_id, { name: v.group_name, priceMode: v.price_mode, count: v.group_product_count ?? 0, options: [] })
      m.get(v.group_id)!.options.push(v)
    }
    return Array.from(m.entries())
  }, [variants])

  if (groups.length === 0) return null

  const toggle = (o: RecipeVariant, on: boolean) => {
    if (on) {
      onChange(o.option_id, customiseDraft(o))
      return
    }
    if (o.has_recipe) setConfirmOff(o)
    else onChange(o.option_id, null)
  }

  return (
    <section className="space-y-3">
      <div>
        <h3 className="text-sm font-semibold">Variants</h3>
        <p className="text-xs text-muted-foreground">
          What changes when an option is chosen. An option&apos;s recipe is shared by every item that uses its group,
          unless you set it for this item only.
        </p>
      </div>
      {groups.map(([groupId, g]) => (
        <div key={groupId} className="rounded-lg border">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b bg-muted/40 px-3 py-2">
            <span className="text-sm font-medium">{g.name}</span>
            <span className="text-xs text-muted-foreground">
              {g.options.length} option{g.options.length === 1 ? '' : 's'}
              {g.count > 1 ? ` · shared by ${g.count} items` : ''}
            </span>
          </div>
          <ul className="divide-y">
            {g.options.map((o) => {
              const draft = drafts[o.option_id]
              const customised = Boolean(draft)
              const cap = optionPrice(o, price)
              const totals = draft ? variantCost(baseLines, yieldN, draft, items, cap) : null
              const savedCost = o.cost
              return (
                <li key={o.option_id} className="px-3 py-2.5">
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <div className="min-w-0 flex-1">
                      <span className="text-sm font-medium">{o.option_name}</span>
                      {o.is_default && <Badge variant="outline" className="ml-2 text-[10px]">default</Badge>}
                      <span className="ml-2 text-xs tabular-nums text-muted-foreground">
                        {g.priceMode === 'replace' ? formatMoney(o.price_delta) : o.price_delta > 0 ? `+${formatMoney(o.price_delta)}` : 'no extra charge'}
                      </span>
                    </div>
                    <span className="text-xs tabular-nums text-muted-foreground">
                      {totals ? (
                        totals.uncosted > 0 ? (
                          <span className="text-amber-700 dark:text-amber-300">
                            incomplete — {totals.uncosted} ingredient{totals.uncosted === 1 ? '' : 's'} without a cost · so far {formatMoney(totals.cost)}
                          </span>
                        ) : (
                          <>
                            cost <span className={cn('font-medium', totals.loss ? 'text-destructive' : 'text-foreground')}>{formatMoney(totals.cost)}</span>
                            {cap > 0 && <> · {totals.marginPct.toFixed(0)}% margin</>}
                            <span className="text-muted-foreground"> (dine-in, one option)</span>
                          </>
                        )
                      ) : savedCost != null ? (
                        <>same as base · cost {formatMoney(savedCost)}</>
                      ) : (
                        'same as base'
                      )}
                    </span>
                    <label className="flex items-center gap-2 text-xs">
                      <Switch
                        checked={customised}
                        onCheckedChange={(on) => toggle(o, on)}
                        aria-label={`Customise the recipe for ${o.option_name}`}
                      />
                      Customise
                    </label>
                  </div>
                  {draft && (
                    <div className="mt-3 space-y-3 rounded-md bg-muted/30 p-3">
                      <div className="flex flex-wrap items-center gap-2">
                        {g.count > 1 && (
                          <label className="mr-3 flex items-center gap-2 text-xs">
                            <Switch
                              checked={Boolean(draft.onlyThisItem)}
                              onCheckedChange={(on) => onChange(o.option_id, { ...draft, onlyThisItem: on })}
                              aria-label={`Use this ${o.option_name} recipe on this item only`}
                            />
                            {draft.onlyThisItem ? 'This item only' : `All ${g.count} items`}
                          </label>
                        )}
                        <Label htmlFor={`mult-${o.option_id}`} className="text-xs">Makes</Label>
                        <Input
                          id={`mult-${o.option_id}`}
                          type="number"
                          min={0}
                          step="any"
                          inputMode="decimal"
                          className={cn('h-8 w-20 tabular-nums', !multiplierValid(draft.multiplier) && 'border-destructive')}
                          value={draft.multiplier}
                          onChange={(e) => onChange(o.option_id, { ...draft, multiplier: e.target.value })}
                          aria-invalid={!multiplierValid(draft.multiplier)}
                        />
                        <span className="text-xs text-muted-foreground">× the base recipe (a whole cake cut into 8 slices is 8; leave 1 otherwise)</span>
                      </div>
                      {!multiplierValid(draft.multiplier) && (
                        <p className="text-xs text-destructive">The multiplier must be above 0.</p>
                      )}
                      <RecipeEditor
                        kind="option"
                        lines={draft.lines}
                        onChange={(lines) => onChange(o.option_id, { ...draft, lines })}
                        items={items}
                        itemList={itemList}
                        price={cap}
                        yieldN={1}
                        formatMoney={formatMoney}
                        onCreateIngredient={onCreateIngredient ? (q, k) => onCreateIngredient(o.option_id, q, k) : undefined}
                        onSaveCost={onSaveCost}
                      />
                      <p className="text-xs text-muted-foreground">
                        <span className="font-medium text-foreground">Adds</span> goes on top of the base recipe (an extra shot).{' '}
                        <span className="font-medium text-foreground">Replaces</span> stands in for the base line of the same ingredient (Iced: milk 180 ml instead of 330).
                      </p>
                    </div>
                  )}
                </li>
              )
            })}
          </ul>
        </div>
      ))}

      <Dialog open={confirmOff != null} onOpenChange={(o) => { if (!o) setConfirmOff(null) }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Remove the {confirmOff?.option_name} recipe?</DialogTitle>
            <DialogDescription>
              {confirmOff && (confirmOff.group_product_count ?? 0) > 1
                ? `Removes the ${confirmOff.option_name} recipe from all ${confirmOff.group_product_count} items that use ${confirmOff.group_name}.`
                : `Removes the ${confirmOff?.option_name ?? ''} recipe from this item.`}{' '}
              Nothing changes until you save.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setConfirmOff(null)}>Keep it</Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => {
                if (confirmOff) onChange(confirmOff.option_id, null)
                setConfirmOff(null)
              }}
            >
              Remove recipe
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  )
}
